import { useSyncExternalStore } from 'react';

/**
 * Qué novedades ya leyó este usuario.
 *
 * Vive en un store propio y no en el estado de un componente porque hay DOS pantallas
 * mirando lo mismo: el contador del menú y el listado de novedades. Al abrir una, las dos
 * tienen que enterarse en el momento — si cada una llevara su copia, el listado seguiría
 * marcándola sin leer hasta recargar.
 *
 * La clave lleva sufijo de versión: lo guardado bajo `imperium_seen_posts` se generó con
 * la regla vieja —pisar el listado marcaba todas de golpe— y diría "leí todo" cuando no
 * se leyó nada. Si vuelve a cambiar la regla, subir la versión otra vez.
 *
 * Es por navegador: cambiar de dispositivo lo reinicia. Para lectura real por usuario
 * habría que persistirla en la base.
 */
const SEEN_KEY = 'imperium_seen_posts_v2';

function leerGuardado(): ReadonlySet<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return new Set<string>(raw ? JSON.parse(raw) : []);
  } catch { return new Set<string>(); }
}

let leidas: ReadonlySet<string> = leerGuardado();
const oyentes = new Set<() => void>();

const subscribe = (fn: () => void) => { oyentes.add(fn); return () => { oyentes.delete(fn); }; };
/** Referencia estable mientras no cambie: useSyncExternalStore la compara por identidad. */
const getSnapshot = () => leidas;

/** Marca una novedad como leída. Idempotente: si ya estaba, no re-renderiza a nadie. */
export function marcarNovedadLeida(id: string) {
  if (!id || leidas.has(id)) return;
  const next = new Set(leidas);
  next.add(id);
  leidas = next;
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...next])); } catch { /* modo privado */ }
  oyentes.forEach((fn) => fn());
}

/**
 * Marca varias de una. Es el "ya las vi todas" del listado.
 *
 * Hace falta además del corte que guarda el backend porque son dos marcas distintas: el
 * backend decide el CONTADOR (desde cuándo cuenta) y esto decide el chip "Sin leer" de cada
 * fila, que sale de acá. Sin las dos, el contador bajaba a cero y las filas seguían
 * marcadas. (13/08/2026)
 */
export function marcarNovedadesLeidas(ids: string[]) {
  const nuevos = ids.filter((id) => id && !leidas.has(id));
  if (!nuevos.length) return;
  const next = new Set(leidas);
  for (const id of nuevos) next.add(id);
  leidas = next;
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...next])); } catch { /* modo privado */ }
  oyentes.forEach((fn) => fn());
}

/** Novedades leídas, reactivo. */
export function useNovedadesLeidas(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Solo para tests: vuelve el store al estado inicial. */
export function _resetNovedadesLeidas() {
  leidas = leerGuardado();
  oyentes.forEach((fn) => fn());
}
